<template>
  <div style="max-width: 960px; margin: 0 auto 5rem auto">
    <HeaderBar
      view="save"
      :showBar="showHeaderBar"
      @discard="discardModalOpen = true"
      @save="saveChanges($route.params.id)"
    />
    <div class="dashboard-title mb-lg">
      <BackButton link="/agency/our-public-profiles/" text="Back" />
      <ProfileHeading
        :heading="`${profile.vendorName} Directory Profile`"
        :main-badge="{
          text: badgeStatusFilter('status'),
          class: `${badgeStatusFilter('statusClass')}`,
          colors: {
            hiddenStatus: '#fff',
            searchable: '#a4e8f2',
            public: '#aee9d1',
            featured: '#fff9c6',
          },
        }"
        :profile-link="{
          href: previewLink,
          text: previewProfileText,
          showPreviewIcon: profile.status === 'H' || profile.status === 'N',
        }"
      />
    </div>

    <hr class="horizontal-line" />

    <!--
      About Section
    -->
    <Card>
      <CardHeading heading="About" :showCapacity="false" :showButton="false" />
      <div class="grid" style="padding: 0 1.25rem">
        <div class="mb-3">
          <Input
            :value="profile.renderName"
            @input-change="updateProfile"
            id="agencyCompanyName"
            name="name"
            label="Company Name"
            placeholder="Your company name"
            :required="true"
            :key="nameKey"
          />
        </div>
        <div class="mb-3">
          <Input
            :value="profile.renderWebsite"
            @input-change="updateProfile"
            id="agencyCompanyWebsite"
            name="renderWebsite"
            label="Company Website"
            :field-validator="validateWebsite"
            placeholder="https://yourwebsite.com"
            :required="true"
            :key="websiteKey"
          />
        </div>
        <div class="mb-3">
          <Input
            :value="profile.renderAboutUs"
            @input-change="updateProfile"
            id="agencyCompanyAbout"
            name="renderAboutUs"
            type="texteditor"
            :limit="1000"
            placeholder="Your company description"
            label="Company Description"
            :required="true"
            :key="descriptionKey"
          />
        </div>
        <div class="mb-3">
          <FileUploader
            :existingImg="profile.renderImg || ''"
            @update-image="updateImage"
            id="agencyCompanyLogo"
            field="renderImg"
            buttonText="Replace image"
            buttonType="ghost"
            :forceUpload="true"
            :required="true"
            label="Company Logo"
            zoneText="Click or drag here to upload an image"
            accept=".png, .jpg, .jpeg, .gif"
            uploadDescription="<p class='fs-sm' style='color: #727a88;'><span style='font-weight: 600;'>File type:</span> JPG, PNG | <span style='font-weight: 600'>Min-width:</span> 400px | <span style='font-weight: 600'>Background:</span> Transparent</p>"
          />
        </div>
      </div>
    </Card>

    <!--
      Locations Section
    -->
    <div>
      <Card>
        <CardHeading
          heading="Primary Location"
          :showCapacity="false"
          :showButton="false"
        />
        <div class="grid">
          <div class="grid__cell col4-of-12">
            <Input
              label="Country"
              :showSearchSelect="true"
              :selected="agencyProfile.country"
              placeholder="Select a country"
              :options="countryOptions"
              type="select"
              id="agencyCompanyCountry"
              :required="true"
              @input-change="updateCountry"
              :key="countryKey"
            />
          </div>
          <div class="grid__cell col4-of-12">
            <Input
              label="State/Province"
              placeholder="Select a state/province"
              :showSearchSelect="true"
              type="select"
              id="agencyCompanyState"
              :required="agencyProfile.country === 'US'"
              :disabled="agencyProfile.country !== 'US'"
              :selected="agencyProfile.state"
              :options="stateOptions"
              @input-change="updateState"
              :key="stateKey"
            />
          </div>
          <div class="grid__cell col4-of-12">
            <Input
              id="agencyCompanyCity"
              :value="agencyProfile.city"
              @input-change="updateCity"
              label="City"
              :required="true"
              placeholder="City name"
              :key="cityKey"
            />
          </div>
        </div>
      </Card>

      <Card v-if="profile.locationsEnabled">
        <CardHeading
          heading="Additional Locations"
          :showCapacity="false"
          :showButton="editingLocations ? false : true"
          buttonText="Add Location"
          @button-click="
            editingLocations = {
              new: true,
              data: { country: {}, region: {}, city: {} },
            }
          "
        />
        <div v-if="!editingLocations && locations[0]">
          <ProfileContentItem
            v-for="(location, i) in locations"
            @edit-click="
              handleAdditionalLocationEdit({
                new: false,
                data: { ...location, order: i },
              })
            "
            @delete-click="
              deletePrep({
                item: { ...location, order: i },
                section: 'locations',
                modalTitle: 'Delete location?',
                modalDescription:
                  'This action cannot be undone, are you sure you want to delete this location?',
              })
            "
            :reorderable="false"
            :title="
              `${
                location.region.key
                  ? location.city.key
                    ? location.city.key + ','
                    : 'All Cities, '
                  : ''
              } ${
                location.region.key
                  ? location.region.key
                  : 'All States/Provinces'
              }, ${location.country.key}`
            "
            :key="i"
          />
        </div>
        <CardText
          v-if="!editingLocations && !locations[0]"
          text="No locations have been added."
        />
        <CardFormWrapper v-if="editingLocations">
          <div class="grid">
            <div class="grid__cell col4-of-12">
              <Input
                label="Country"
                :selected="editingLocations.data.country.value"
                :showSearchSelect="true"
                id="additional-country"
                :options="additionalCountryOptions"
                placeholder="Select a country"
                type="select"
                @input-change="updateAdditionalCountry"
              />
            </div>
            <div class="grid__cell col4-of-12">
              <Input
                label="State/Province"
                :selected="editingLocations.data.region.value"
                :showSearchSelect="true"
                id="additional-state"
                :options="additionalStateOptions"
                placeholder="Select State/Province"
                type="select"
                @input-change="updateAdditionalState"
              />
            </div>
            <div class="grid__cell col4-of-12">
              <Input
                label="City"
                :selected="editingLocations.data.city.value"
                :showSearchSelect="true"
                id="additional-city"
                :options="additionalCityOptions"
                placeholder="Select city"
                type="select"
                @input-change="updateAdditionalCity"
              />
            </div>
          </div>
          <div class="flex" style="justify-content: flex-end; margin-top: 1rem">
            <Button @button-click="editingLocations = false" bType="secondary">
              Cancel
            </Button>
            <div>
              <Button
                :bDisabled="!locationsFormComplete"
                @button-click="
                  handleDoneClick({
                    item: 'editingLocations',
                    section: 'locations',
                  })
                "
              >
                Done
              </Button>
            </div>
          </div>
        </CardFormWrapper>
      </Card>
    </div>

    <!--
      Testimonials Section
    -->
    <div style="position: relative">
      <Tooltip
        v-if="profile.reviewsEnabled && showTestimonialsTooltip"
        css="left: 30%; bottom: -130px; z-index: 999;"
        fontClass="text-sm"
        message="Testimonials have been disabled as Reviews are enabled on this Directory"
      />
    </div>
    <div
      :class="profile.reviewsEnabled ? 'disabled' : ''"
      @mouseover="showTestimonialsTooltip = true"
      @mouseleave="showTestimonialsTooltip = false"
    >
      <QuoteContentSection
        :reviews-enabled="profile.reviewsEnabled"
        heading="Testimonials"
        namespace="ourPublicProfilesDetail"
        :agencyOPP="true"
        :storeInfo="{
          get: 'testimonials',
          set: 'setTestimonials',
          add: 'addItem',
          edit: 'editItem',
          delete: 'deleteItem',
          dirty: 'markSectionDirty',
        }"
        @delete-content="deletePrep"
      />
    </div>

    <!--
      Featured Content Section
    -->
    <div v-if="profile.showcaseEnabled">
      <MediaContentSection
        namespace="ourPublicProfilesDetail"
        heading="Past Work"
        sectionType="pastWork"
        :storeInfo="{
          get: 'pastWork',
          set: 'updatePastWork',
          add: 'addItem',
          edit: 'editItem',
          delete: 'deleteItem',
          dirty: 'markSectionDirty',
        }"
        @delete-content="deletePrep"
      />
      <VideoContentSection
        namespace="ourPublicProfilesDetail"
        @delete-content="deletePrep"
        :videoLimit="3"
        sectionType="videos"
        heading="Videos"
        :storeInfo="{
          get: 'videos',
          set: 'updateVideos',
          add: 'addItem',
          edit: 'editItem',
          delete: 'deleteItem',
          dirty: 'markSectionDirty',
        }"
      />
    </div>
    <!--
      Clients Section
    -->
    <ClientLogoContentSection
      heading="Client Logos"
      namespace="ourPublicProfilesDetail"
      :agencyOPP="true"
      :storeInfo="{
        get: 'clients',
        set: 'updateClients',
        add: 'addItem',
        edit: 'editItem',
        delete: 'deleteItem',
        dirty: 'markSectionDirty',
      }"
      @delete-content="deletePrep"
    />

    <!--
      Resources Section
    -->

    <ResourcesContentSection
      heading="Resources"
      namespace="ourPublicProfilesDetail"
      :agencyOPP="true"
      :storeInfo="{
        get: 'resources',
        set: 'updateResources',
        add: 'addItem',
        edit: 'editItem',
        delete: 'deleteItem',
        dirty: 'markSectionDirty',
      }"
      @delete-content="deletePrep"
    />

    <!--
      Filters
    -->

    <FiltersContentSection
      heading="Filters"
      namespace="ourPublicProfilesDetail"
      :agencyOPP="true"
      :storeInfo="{
        get: 'profile',
      }"
    />

    <div class="flex justify-end mt-8">
      <Button @button-click="saveChanges($route.params.id)"> Save </Button>
    </div>

    <Modal
      v-if="modalOpen"
      :old="false"
      :newButtons="true"
      :modalShowing="modalOpen"
      :title="modalOpen.title"
      :description="modalOpen.description"
      @close-modal="modalOpen = false"
      @primary-click="confirmedDelete"
      @secondary-click="modalOpen = false"
    />
    <Modal
      v-if="discardModalOpen"
      :old="false"
      :modalShowing="discardModalOpen"
      title="Discard changes?"
      :newButtons="true"
      description="Are you sure you want to discard all the changes?"
      @close-modal="discardModalOpen = false"
      primaryText="Discard changes"
      secondaryText="Cancel"
      @primary-click="discardChanges"
      @secondary-click="discardModalOpen = false"
    />
  </div>
</template>

<script>
import Card from '@/components/DesignSystem/Card';
import Input from '@/components/DesignSystem/Input';
import Modal from '@/components/DesignSystem/Modal';
import Button from '@/components/DesignSystem/Button';
import CardHeading from '@/components/DesignSystem/CardHeading';
import FileUploader from '@/components/DesignSystem/FileUploader';
import CardText from '@/components/DesignSystem/CardText';
import CardFormWrapper from '@/components/DesignSystem/CardFormWrapper';
import ProfileContentItem from '@/components/Shared/ProfileContentItem';
import HeaderBar from '@/components/DesignSystem/HeaderBar';
import vendorDirectoryAPI from '@/utils/api/vendorDirectory';
import Tooltip from '@/components/DesignSystem/Tooltip';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { validateWebsite } from '@/components/DesignSystem/validators';
import BackButton from '@/components/BackButton';
import { handleFileChange } from '@/utils/files';
import Vue from 'vue';
import QuoteContentSection from '@/components/Shared/ContentSection/QuoteContentSection';
import ClientLogoContentSection from '@/components/Shared/ContentSection/ClientLogoContentSection';
import ResourcesContentSection from '@/components/Shared/ContentSection/ResourcesContentSection';
import FiltersContentSection from '@/components/Shared/ContentSection/FiltersContentSection';
import MediaContentSection from '@/components/Shared/ContentSection/MediaContentSection';
import VideoContentSection from '@/components/Shared/ContentSection/VideoContentSection';
import ProfileHeading from '@/components/Shared/ContentSection/ProfileHeading';

export default {
  components: {
    ProfileHeading,
    VideoContentSection,
    MediaContentSection,
    FiltersContentSection,
    ResourcesContentSection,
    ClientLogoContentSection,
    QuoteContentSection,
    HeaderBar,
    Card,
    Modal,
    Input,
    Button,
    CardHeading,
    CardText,
    FileUploader,
    CardFormWrapper,
    ProfileContentItem,
    Tooltip,
    BackButton,
  },
  data() {
    return {
      showTestimonialsTooltip: false,
      toDelete: undefined,
      discardModalOpen: false,
      modalOpen: false,
      editingLocations: false,
      nameKey: 1,
      descriptionKey: 2,
      websiteKey: 3,
      countryKey: 14,
      stateKey: 5,
      cityKey: 6,
      directoryData: undefined,
    };
  },
  filters: {
    status(value) {
      switch (value) {
        case 'I':
          return 'Invited';
        case 'P':
          return 'Public';
        case 'H':
          return 'Hidden';
        case 'S':
          return 'Searchable';
        case 'N':
          return 'Hidden';
        default:
          return 'Invited';
      }
    },
    statusClass(value) {
      switch (value) {
        case 'I':
          return 'invited';
        case 'P':
          return 'public';
        case 'H':
          return 'hiddenStatus';
        case 'S':
          return 'searchable';
        case 'N':
          return 'hiddenStatus';
        default:
          return 'invited';
      }
    },
    filterPdf(value) {
      if (
        value.slice(-4) === '.pdf' ||
        value.slice(0, 20) === 'data:application/pdf'
      ) {
        return '';
      }
      return value;
    },
  },

  computed: {
    ...mapGetters({
      profile: 'ourPublicProfilesDetail/profile',
      filters: 'ourPublicProfilesDetail/filters',
      cities: 'ourPublicProfilesDetail/cities',
      states: 'ourPublicProfilesDetail/states',
      countries: 'ourPublicProfilesDetail/countries',
      testimonials: 'ourPublicProfilesDetail/testimonials',
      showHeaderBar: 'ourPublicProfilesDetail/showHeaderBar',
    }),

    locationsFormComplete() {
      if (this.editingLocations && this.editingLocations.data) {
        if (
          Object.keys(this.editingLocations.data.region).length &&
          Object.keys(this.editingLocations.data.country).length
        ) {
          return true;
        }
      }
      return false;
    },
    previewLink() {
      let url = '';
      if (this.directoryData) {
        if (this.directoryData.directorySettings.customDomain) {
          url += this.directoryData.directorySettings.customDomain;
        } else {
          url +=
            'http://' +
            this.directoryData.vendorData.slug +
            '.' +
            process.env.VUE_APP_ROOT_CLIENT;
        }

        if (this.directoryData.directorySettings.directoryPage) {
          url += '/' + this.directoryData.directorySettings.directoryPage;
        }

        if (this.profile.status === 'H' || this.profile.status === 'N') {
          url +=
            '/preview/' + this.profile.resellerSlug + '/' + this.profile.id;
        } else {
          url += '/' + this.profile.resellerSlug;
        }
      }
      return url;
    },

    previewProfileText() {
      if (this.profile.status === 'H' || this.profile.status === 'N') {
        return 'Preview profile';
      }
      return 'View profile';
    },

    agencyProfile() {
      return this.$store.getters['agencyProfile'];
    },

    additionalCountryOptions() {
      return this.countries.map(country => {
        return {
          name: country.name,
          value: country.id,
        };
      });
    },

    additionalStateOptions() {
      var options = [
        {
          name: 'All States/Provinces',
          value: null,
        },
      ];
      this.states.map(state => {
        return options.push({
          name: state.name,
          value: state.id,
        });
      });
      return options;
    },

    additionalCityOptions() {
      var options = [
        {
          name: 'All Cities',
          value: null,
        },
      ];
      if (this.cities.length) {
        this.cities.map(city => {
          return options.push({
            name: city.name,
            value: city.id,
          });
        });
      }
      return options;
    },

    countryOptions() {
      return this.agencyProfile.countryChoices.map(choice => {
        return {
          name: choice[1],
          value: choice[0],
        };
      });
    },

    stateOptions() {
      return this.agencyProfile.choices.map(choice => {
        return {
          name: choice[1],
          value: choice[0],
        };
      });
    },

    locations: {
      get() {
        return this.$store.getters['ourPublicProfilesDetail/locations'].filter(
          i => !i.delete
        );
      },
      set(value) {
        this.$store.commit('ourPublicProfilesDetail/setLocations', value);
      },
    },

    exisitingLocation() {
      const { countryName, state, city } = this.$store.getters['agencyProfile'];
      return { countryName, state, city };
    },

    dragOptions() {
      return {
        animation: 200,
        ghostClass: 'ghost',
      };
    },
  },

  async created() {
    await this.fetchData();
    const data = await vendorDirectoryAPI.getDirectoryInfo(
      this.profile.directory
    );
    this.directoryData = data;
  },

  beforeRouteLeave(to, from, next) {
    this.discardChanges(false);
    next();
  },

  methods: {
    ...mapActions({
      fetchProfile: 'ourPublicProfilesDetail/fetchProfile',
      fetchCountries: 'ourPublicProfilesDetail/fetchCountries',
      fetchStates: 'ourPublicProfilesDetail/fetchStates',
      fetchCities: 'ourPublicProfilesDetail/fetchCities',
      fetchShowcase: 'ourPublicProfilesShowcases/fetchShowcase',
      fetchLocations: 'ourPublicProfilesDetail/fetchLocations',
      fetchClients: 'ourPublicProfilesDetail/fetchClients',
      clearCities: 'ourPublicProfilesDetail/clearCities',
      fetchFeaturedContent: 'ourPublicProfilesDetail/fetchFeaturedContent',
      fetchResources: 'ourPublicProfilesDetail/fetchResources',
      fetchTestimonials: 'ourPublicProfilesDetail/fetchTestimonials',
      saveChanges: 'ourPublicProfilesDetail/saveChanges',
      updateImage: 'ourPublicProfilesDetail/updateImage',
      updateProfile: 'ourPublicProfilesDetail/updateProfile',
      updateError: 'ourPublicProfilesDetail/updateError',
      deleteItem: 'ourPublicProfilesDetail/deleteItem',
      updateProfileLocation: 'ourPublicProfilesDetail/updateProfileLocation',
      addItem: 'ourPublicProfilesDetail/addItem',
      editItem: 'ourPublicProfilesDetail/editItem',
      handleCheckboxChange: 'ourPublicProfilesDetail/handleCheckboxChange',
      handleDropdownChange: 'ourPublicProfilesDetail/handleDropdownChange',
    }),

    validateWebsite,

    async fetchData() {
      await this.fetchProfile(this.$route.params.id);
      this.fetchCountries();
      this.fetchShowcase(this.$route.params.id);
      this.fetchLocations();
      this.fetchClients(this.$route.params.id);
      this.fetchFeaturedContent(this.$route.params.id);
      this.fetchResources(this.$route.params.id);
      this.fetchTestimonials(this.$route.params.id);
      return Promise.resolve(true);
    },

    async discardChanges(dataFetch = true) {
      if (dataFetch) {
        this.fetchData();
        await this.$store.dispatch('fetchAgencyProfileInformation');
        this.$store.dispatch('ourPublicProfilesDetail/clearToDelete');
      }

      this.$store.commit('ourPublicProfilesDetail/clearDirty');
      this.nameKey += 1;
      this.descriptionKey += 1;
      this.websiteKey += 1;
      this.countryKey += 1;
      this.stateKey += 1;
      this.cityKey += 1;

      this.discardModalOpen = false;
    },
    ...mapMutations({
      markSectionDirty: 'ourPublicProfilesDetail/markSectionDirty',
    }),

    openPreviewModal() {
      this.modalOpen = true;
    },

    handleFileChange,

    handleEdit(payload) {
      Vue.set(
        this[payload.section]['data'],
        payload.data.key,
        payload.data.value
      );
      if (payload.uri) {
        Vue.set(this[payload.section]['data'], 'uri', payload.uri);
      }
      this.$forceUpdate();
    },

    async handleAdditionalLocationEdit(payload) {
      await this.fetchStates(payload.data.country.value);
      await this.fetchCities({
        stateId: payload.data.region.value,
        countryId: payload.data.country.value,
      });
      this.editingLocations = payload;
    },

    handleDoneClick({ item, section }) {
      if (this[item]['new']) {
        this.addItem({ section, data: this[item]['data'] });
      } else {
        this.editItem({ section, data: this[item]['data'] });
      }
      return (this[item] = false);
    },

    updateCountry(payload) {
      this.updateProfileLocation({
        value: payload.value,
        key: 'country',
      });

      if (payload.value !== 'US') {
        this.updateProfileLocation({
          value: '',
          key: 'state',
        });
      }
    },

    updateState(payload) {
      this.updateProfileLocation({
        value: payload.value,
        key: 'state',
      });
    },

    updateCity(payload) {
      this.updateProfileLocation({
        value: payload.value,
        key: 'city',
      });
    },

    updateAdditionalCountry(payload) {
      this.editingLocations.data = {
        ...this.editingLocations.data,
        country: payload,
        region: '',
        city: '',
      };

      if (!this.editingLocations.new) {
        this.editingLocations.data = {
          ...this.editingLocations.data,
          update: true,
        };
      }
      this.fetchStates(payload.value);
    },

    updateAdditionalState(payload) {
      this.editingLocations.data = {
        ...this.editingLocations.data,
        region: payload,
        city: '',
      };
      if (!this.editingLocations.new) {
        this.editingLocations.data = {
          ...this.editingLocations.data,
          update: true,
        };
      }
      if (payload.value != null) {
        this.fetchCities({
          stateId: payload.value,
          countryId: this.editingLocations.data.country.value,
        });
      } else {
        this.clearCities();
      }
    },

    updateAdditionalCity(payload) {
      this.editingLocations.data = {
        ...this.editingLocations.data,
        city: payload,
      };
      if (!this.editingLocations.new) {
        this.editingLocations.data = {
          ...this.editingLocations.data,
          update: true,
        };
      }
    },

    deletePrep(payload) {
      this.toDelete = payload;
      this.modalOpen = {
        title: payload.modalTitle,
        description: payload.modalDescription,
      };
    },

    confirmedDelete() {
      this.$store.dispatch('ourPublicProfilesDetail/deleteItem', this.toDelete);
      this.modalOpen = false;
      this.toDelete = false;
    },
    hideTestimonialsTooltip() {
      setTimeout(() => {
        this.showTestimonialsTooltip = false;
      }, 500);
    },

    badgeStatusFilter(filter) {
      return this.$options.filters[filter](this.profile.status);
    },
  },
};
</script>

<style lang="scss" scoped>
.disabled {
  cursor: not-allowed !important;
}

.route-enter-active,
.route-leave-active {
  transition-duration: 0.1s;
  transition-property: all;
  transition-timing-function: ease;
}

.route-enter-active {
  transition-delay: 0.1s;
}

.route-enter,
.route-leave-active {
  opacity: 0;
}

.fa-angle-left {
  margin-right: 5px;
}

.preview-button {
  margin-top: 1rem;
  color: #4d48e8;
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;

  svg {
    margin-right: 5px;
    fill: #4d48e8;
  }
}

.horizontal-line {
  color: #dadee6;
  margin: 2rem 0;
}

.ghost {
  opacity: 0;
}
.button-type-primary {
  margin-left: 8px;
}
</style>
